import { Container, Graphics, Rectangle, Text, TextMetrics, TextStyle } from "pixi.js"

import { DISPLAY_FONT_FAMILY } from "../../text/typography"
import { GOLD, Sparks } from "../ember_atmosphere"
import { easeOutCubic } from "../../intro/logo_choreography"
import { BuilderPage, PageId } from "./page"
import { ACCENT, BODY_H, BODY_Y, CONTENT_W, RIM_DIM, TEXT, TEXT_DIM } from "./theme"
import { capsText, drawGlassPanel, lensRule, text } from "./widgets"

// The page before the forge: the exile that puts you on the road.

const TYPE_MS_PER_CHAR = 22

// A block of prose that types itself out. Pre-wrapped with TextMetrics so revealing a character at a
// time can't reflow the paragraph underneath itself (the usual word-wrap typewriter jitter). Lines are
// centred on their FINISHED width, so a line grows outward from where it will end up rather than
// sliding left as it fills.
class Typewriter extends Container {
    private lines: { label: Text; full: string; x: number }[] = []
    private caret = new Graphics()
    private total = 0
    private revealed = -1
    private elapsed: number
    private finished = false
    private readonly lineH: number

    constructor(
        body: string,
        width: number,
        { size = 17, color = TEXT, lineHeight = 27, startMs = 0 }: { size?: number; color?: number; lineHeight?: number; startMs?: number } = {},
    ) {
        super()
        this.lineH = lineHeight
        this.elapsed = startMs
        const style = new TextStyle({
            fontFamily: DISPLAY_FONT_FAMILY,
            fontSize: size,
            fontWeight: "400",
            wordWrap: true,
            wordWrapWidth: width,
            // Match what text() below actually renders, so the lines it measures are the lines drawn.
            strokeThickness: 0,
        })
        const measured = TextMetrics.measureText(body, style)
        measured.lines.forEach((line, i) => {
            const label = text("", { display: true, weight: "400", size, color })
            const full = line.trimEnd()
            const lineWidth = TextMetrics.measureText(full, style).width
            label.y = i * lineHeight
            this.addChild(label)
            this.lines.push({ label, full, x: Math.round((width - lineWidth) / 2) })
            this.total += full.length
        })
        this.caret.beginFill(GOLD, 0.9)
        this.caret.drawRect(0, 0, 2, size + 3)
        this.caret.endFill()
        this.addChild(this.caret)
        this.animate(0)
    }

    /** The block's height when fully typed -- known up front, so the panel can be sized to it. */
    get blockHeight(): number {
        return this.lines.length * this.lineH
    }

    get done(): boolean {
        return this.finished
    }

    get elapsedMs(): number {
        return this.elapsed
    }

    /** Type the rest at once -- the player clicked to hurry it along. */
    finish = () => {
        this.elapsed = Math.max(this.elapsed, this.total * TYPE_MS_PER_CHAR)
        this.animate(0)
    }

    animate(dtMs: number) {
        this.elapsed += dtMs
        const target = Math.min(this.total, Math.floor(this.elapsed / TYPE_MS_PER_CHAR))
        if (target !== this.revealed) {
            this.revealed = target
            let remaining = target
            this.lines.forEach(line => {
                const take = Math.max(0, Math.min(line.full.length, remaining))
                if (line.label.text.length !== take) {
                    line.label.text = line.full.slice(0, take)
                }
                line.label.x = line.x
                remaining -= line.full.length
            })
            this.finished = target >= this.total
        }

        // The caret rides the end of the line being typed, then blinks twice and goes.
        const typing = !this.finished
        const afterMs = this.elapsed - this.total * TYPE_MS_PER_CHAR
        this.caret.visible = typing || (afterMs < 1100 && Math.floor(afterMs / 275) % 2 === 1)
        const current =
            this.lines.find(line => line.label.text.length < line.full.length) || this.lines[this.lines.length - 1]
        if (current) {
            this.caret.x = current.x + current.label.width + 2
            this.caret.y = current.label.y + 3
        }
    }
}

// The seal has to read as wax pressed onto the decree, not as something to press. The first one was a
// toothed disc with a domed highlight and a floating drop shadow, alone and centred under the text,
// with a ring of light pulsing off it: every cue of a button. So the wax is an uneven dab lying flat
// on the page, the die is sunk INTO it, and it sits askew at the end of a sign-off line over two
// ribbon tails, where a seal goes on a document.
const WAX = 0x8a1a12
const WAX_DEEP = 0x4e0b07
const WAX_LIT = 0xc7473a
const WAX_FACE = 0x7a160f
const WAX_RELIEF = 0x93201a
const RIBBON = 0x9b6a2c
const RIBBON_DEEP = 0x5e3d18
const RIBBON_LIT = 0xd9a456
const SEAL_SEED = 2.2

/**
 * The outline of a dab of wax about (0, 0): round-ish but swelling unevenly, with two lobes where it
 * squeezed out from under the die. `seed` turns the unevenness.
 */
const waxOutline = (r: number, seed: number): number[] => {
    const lobe = (angle: number, at: number, width: number, height: number) => {
        const d = Math.atan2(Math.sin(angle - at), Math.cos(angle - at))
        return height * Math.exp(-(d * d) / (width * width))
    }
    const points: number[] = []
    for (let i = 0; i < 96; i++) {
        const a = (i / 96) * Math.PI * 2
        const swell =
            0.06 * Math.sin(a * 2 + seed * 1.7) +
            0.05 * Math.sin(a * 3 + seed * 2.9) +
            0.035 * Math.sin(a * 5 + seed * 4.3) +
            0.02 * Math.sin(a * 9 + seed * 0.7) +
            lobe(a, 0.6 + seed, 0.28, 0.13) +
            lobe(a, 3.6 + seed, 0.22, 0.09)
        points.push(Math.cos(a) * r * (1 + swell), Math.sin(a) * r * (1 + swell))
    }
    return points
}

const starPoints = (radius: number, inner: number, dx: number, dy: number): number[] => {
    const points: number[] = []
    for (let i = 0; i < 16; i++) {
        const angle = (i / 16) * Math.PI * 2 - Math.PI / 2
        const rr = i % 2 === 0 ? radius : inner
        points.push(Math.cos(angle) * rr + dx, Math.sin(angle) * rr + dy)
    }
    return points
}

/** The wax's shadow on the page, in black, about (0, 0): its own Graphics so it can trail the seal down. */
const drawSealShadow = (g: Graphics, r: number) => {
    g.beginFill(0x000000, 1)
    g.drawPolygon(waxOutline(r * 1.15, SEAL_SEED))
    g.endFill()
}

/**
 * An imperial seal pressed into red wax, about (0, 0). Lit from the upper left: the bead squeezed up
 * round the die stands proud (lit lip on that side), the die's circle is sunk in (its far wall dark,
 * its near wall lit), and a beaded border and an eight-pointed star stand up in relief inside it.
 */
const drawSeal = (g: Graphics, r: number) => {
    const outline = waxOutline(r * 1.12, SEAL_SEED)
    g.beginFill(WAX, 1)
    g.drawPolygon(outline)
    g.endFill()
    g.beginFill(WAX_LIT, 0.35)
    g.drawPolygon(outline.map(v => v * 0.93 - 0.6))
    g.endFill()
    g.beginFill(WAX, 1)
    g.drawPolygon(outline.map(v => v * 0.93 + 0.5))
    g.endFill()

    const die = r * 0.74
    g.beginFill(WAX_DEEP, 0.9)
    g.drawCircle(-0.9, -0.9, die)
    g.endFill()
    g.beginFill(WAX_LIT, 0.55)
    g.drawCircle(0.9, 0.9, die)
    g.endFill()
    g.beginFill(WAX_FACE, 1)
    g.drawCircle(0, 0, die - 1.3)
    g.endFill()

    for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2
        const x = Math.cos(angle) * (die - 3.4)
        const y = Math.sin(angle) * (die - 3.4)
        g.beginFill(WAX_LIT, 0.45)
        g.drawCircle(x - 0.35, y - 0.35, 0.85)
        g.endFill()
        g.beginFill(WAX_DEEP, 0.6)
        g.drawCircle(x + 0.35, y + 0.35, 0.85)
        g.endFill()
    }

    g.beginFill(WAX_DEEP, 0.85)
    g.drawPolygon(starPoints(r * 0.44, r * 0.19, 0.8, 0.8))
    g.endFill()
    g.beginFill(WAX_LIT, 0.7)
    g.drawPolygon(starPoints(r * 0.44, r * 0.19, -0.6, -0.6))
    g.endFill()
    g.beginFill(WAX_RELIEF, 1)
    g.drawPolygon(starPoints(r * 0.42, r * 0.18, 0, 0))
    g.endFill()
    g.beginFill(WAX_LIT, 0.6)
    g.drawCircle(-0.4, -0.4, r * 0.09)
    g.endFill()
    g.beginFill(WAX_RELIEF, 1)
    g.drawCircle(0, 0, r * 0.075)
    g.endFill()
}

/** Two swallowtail ribbon ends hanging from (0, 0), fanned apart, for the wax to be pressed over. */
const drawRibbons = (g: Graphics, length: number, width: number, spread: number) => {
    const tail = (angle: number, len: number) => {
        const dx = Math.sin(angle)
        const dy = Math.cos(angle)
        const nx = dy
        const ny = -dx
        const half = width / 2
        const endX = dx * len
        const endY = dy * len
        const notchX = endX - dx * width * 0.7
        const notchY = endY - dy * width * 0.7
        const strip = [
            [nx * half, ny * half],
            [endX + nx * half, endY + ny * half],
            [notchX, notchY],
            [endX - nx * half, endY - ny * half],
            [-nx * half, -ny * half],
        ].flat()
        g.beginFill(0x000000, 0.35)
        g.drawPolygon(strip.map((v, i) => v + (i % 2 === 0 ? 0.8 : 1.6)))
        g.endFill()
        g.beginFill(RIBBON, 1)
        g.drawPolygon(strip)
        g.endFill()
        // A fold of shade down one side, a lit edge down the other.
        g.beginFill(RIBBON_DEEP, 0.55)
        g.drawPolygon([0, 0, notchX, notchY, endX - nx * half, endY - ny * half, -nx * half, -ny * half])
        g.endFill()
        g.lineStyle(1, RIBBON_LIT, 0.6)
        g.moveTo(nx * (half - 0.5), ny * (half - 0.5))
        g.lineTo(endX + nx * (half - 0.5), endY + ny * (half - 0.5))
        g.lineStyle(0)
    }
    tail(spread, length)
    tail(-spread * 0.8, length * 0.88)
}

const SEAL_R = 22
const SEAL_MS = 300
// The seal rests turned a little, as a hand leaves it -- square to the page is how a button sits.
const SEAL_TILT = 0.24
// After the last word: the sign-off line draws out toward the seal's place, the ribbons are laid,
// and then the stamp comes down.
const SIGN_LINE_W = 200
const SEAL_DELAY = 420

const ramp = (ms: number, from: number, over: number) => Math.min(1, Math.max(0, (ms - from) / over))

export class IntroPage extends BuilderPage {
    readonly pageId: PageId = "intro"
    readonly title = "The long exile"

    private typewriter?: Typewriter
    private seal?: Graphics
    private sealShadow?: Graphics
    private signOff?: { line: Graphics; caption: Text; ribbons: Graphics }
    private panel?: Container
    private sparks = new Sparks()
    // Kept across repaints: a resize or a late font rebuilds the page, and the decree should carry on
    // from where it was rather than start typing over, or stamp itself a second time.
    private typedMs = 0
    private sealMs = -1
    private sealedAt?: number

    animate(dtMs: number) {
        this.sparks.update(dtMs)
        const typewriter = this.typewriter
        if (!typewriter) {
            return
        }
        // The decree waits for the page to arrive before it starts writing.
        if (!this.entering) {
            typewriter.animate(dtMs)
            this.typedMs = typewriter.elapsedMs
        }
        if (typewriter.done && this.sealMs < 0) {
            this.sealMs = 0
        }
        // A click only hurries the words along, so only the unfinished decree shows a hand: a pointer
        // still up over the finished seal would say the seal is the thing to press.
        this.cursor = typewriter.done ? "default" : "pointer"
        if (this.sealMs >= 0 && this.seal) {
            this.sealMs += dtMs
            this.paintSignOff(this.sealMs)
            this.paintSeal(this.sealMs - SEAL_DELAY)
        }
    }

    tearDown() {
        this.sparks.clear()
    }

    build() {
        this.wipe()
        this.sparks.clear()
        this.typewriter = undefined

        const panelW = 560 + (CONTENT_W - 780) * 0.5
        const panelX = Math.round((CONTENT_W - panelW) / 2)
        const pad = 34
        const innerW = panelW - pad * 2

        const panel = new Container()
        this.panel = panel
        this.addChild(panel)

        const kicker = capsText("By order of the Emperor", { role: "compactCaption", color: ACCENT, size: 11 })
        const body = new Typewriter(
            "You have been exiled to the barbarous lands of the West, for the Emperor will suffer no rivals in the East.",
            innerW,
            { startMs: this.typedMs },
        )
        // Laid out top-down from the panel's top edge, then the panel is sized to what it holds.
        const kickerTop = pad - 6
        const bodyTop = kickerTop + kicker.height + 24
        const sealCenter = bodyTop + body.blockHeight + 12 + SEAL_R
        const panelH = sealCenter + SEAL_R + pad - 10
        const panelY = Math.round(BODY_Y + (BODY_H - panelH) / 2)

        const glass = new Graphics()
        drawGlassPanel(glass, panelX, panelY, panelW, panelH, { radius: 12 })
        panel.addChild(glass)

        kicker.x = Math.round(panelX + (panelW - kicker.width) / 2)
        kicker.y = panelY + kickerTop
        panel.addChild(kicker)

        const rule = new Graphics()
        lensRule(rule, panelX + panelW / 2, kicker.y + kicker.height + 9, 70)
        panel.addChild(rule)

        body.x = panelX + pad
        body.y = panelY + bodyTop
        panel.addChild(body)
        this.typewriter = body

        // Signed off at the bottom right: a line with the Emperor's mark over it, and the seal pressed at
        // its end over two ribbon tails. The seal and its shadow are drawn about their own centres so
        // the stamp scales down onto its middle.
        const sealX = panelX + panelW - pad - 24
        const sealY = panelY + sealCenter
        const lineRight = sealX - 40
        const line = new Graphics()
        line.beginFill(RIM_DIM, 0.7)
        line.drawRect(0, 0, SIGN_LINE_W, 1)
        line.endFill()
        line.position.set(lineRight - SIGN_LINE_W, sealY + 6)
        panel.addChild(line)
        const caption = capsText("Sealed by his imperial hand", { role: "compactCaption", color: TEXT_DIM, size: 10 })
        caption.x = Math.round(lineRight - caption.width)
        caption.y = Math.round(sealY + 2 - caption.height)
        panel.addChild(caption)
        const ribbons = new Graphics()
        drawRibbons(ribbons, 40, 11, 0.45)
        ribbons.position.set(sealX, sealY + 2)
        panel.addChild(ribbons)
        this.signOff = { line, caption, ribbons }

        const shadow = new Graphics()
        drawSealShadow(shadow, SEAL_R)
        panel.addChild(shadow)
        this.sealShadow = shadow
        const seal = new Graphics()
        drawSeal(seal, SEAL_R)
        seal.position.set(sealX, sealY)
        panel.addChild(seal)
        this.seal = seal
        this.paintSignOff(this.sealMs)
        this.paintSeal(this.sealMs < 0 ? -1 : this.sealMs - SEAL_DELAY)

        this.reveal(glass, 0, { rise: 14, ms: 480 })
        this.reveal(kicker, 160)
        this.reveal(rule, 220, { rise: 0 })

        // Clicking the decree hurries it: finish the words, and let the seal come straight down.
        this.interactive = true
        this.hitArea = new Rectangle(0, BODY_Y, CONTENT_W, BODY_H)
        this.cursor = body.done ? "default" : "pointer"
        this.removeAllListeners("pointerdown")
        this.on("pointerdown", () => {
            this.finishEntrance()
            if (!this.typewriter?.done) {
                this.typewriter?.finish()
                this.typedMs = this.typewriter?.elapsedMs ?? this.typedMs
            }
        })

        this.host.setNavButtons([
            {
                title: "Forge your character ▸",
                primary: true,
                callback: () => this.host.switchPage("build"),
            },
        ])
    }

    /**
     * The sign-off at `ms` after the last word: hidden before; the line drawn out left to right toward
     * the seal's place, then its caption. The ribbon tails spring out from under the wax as it lands --
     * laid out ahead of it, their tucked-in ends showed as a bare chevron.
     */
    private paintSignOff(ms: number) {
        const signOff = this.signOff
        if (!signOff) {
            return
        }
        const { line, caption, ribbons } = signOff
        line.visible = caption.visible = ribbons.visible = ms >= 0
        line.scale.x = easeOutCubic(ms / 320)
        caption.alpha = ramp(ms, 100, 280)
        const landing = SEAL_DELAY + SEAL_MS - 40
        ribbons.alpha = ramp(ms, landing, 60)
        ribbons.scale.y = 0.5 + 0.5 * easeOutCubic(ramp(ms, landing, 240))
    }

    /**
     * The seal at `ms` into its fall: hidden before; coming down from above the page, large and turned,
     * onto a shadow that tightens under it as it nears; then the wax squeezes out under the die and it
     * lies still. The landing is a thump and a few sparks -- no ring of light, which reads as "press me".
     */
    private paintSeal(ms: number) {
        const seal = this.seal
        const shadow = this.sealShadow
        if (!seal || !shadow) {
            return
        }
        seal.visible = shadow.visible = ms >= 0
        if (ms < 0) {
            return
        }
        const t = Math.min(1, ms / SEAL_MS)
        // Gathering speed as it drops, rather than floating in.
        const drop = t * t
        if (t >= 1 && this.sealedAt === undefined) {
            this.sealedAt = ms
            this.sparks.throw(this.panel || this, seal.x, seal.y, 14, 90)
        }
        const since = this.sealedAt === undefined ? -1 : ms - this.sealedAt
        const squeeze = since >= 0 && since < 180 ? 0.07 * Math.sin((Math.PI * since) / 180) : 0
        seal.scale.set(1.7 - 0.7 * drop + squeeze)
        seal.alpha = Math.min(1, t * 2.5)
        seal.rotation = SEAL_TILT - 0.2 * (1 - drop)

        // High up, the shadow is faint, loose, and well down and to the right of it; on landing it
        // closes up into the thin dark rim of wax a few millimetres thick.
        const height = 1 - drop
        shadow.position.set(seal.x + 0.6 + 7 * height, seal.y + 1.4 + 11 * height)
        shadow.scale.set(1 + 0.25 * height + squeeze)
        shadow.rotation = seal.rotation
        shadow.alpha = (0.45 - 0.33 * height) * seal.alpha

        // The thump: the panel gives a little under the stamp.
        if (this.panel) {
            this.panel.y = since >= 0 && since < 220 ? 2.5 * (1 - since / 220) * Math.cos(since / 22) : 0
        }
    }
}
